// Renders one QuestionDef. Single-select types advance on tap; entry types (date, pain,
// goal, zip, email) render a Continue/submit and advance when it's pressed. All answer
// writes go up through onAnswer so App owns persistence + navigation.
import { useState } from 'react';
import type { QuestionDef, ResolvedQuestion } from '../lib/flow';
import { resolveQuestion } from '../lib/flow';
import type { Answers } from '../lib/session';
import { useIsNarrow } from '../lib/useIsNarrow';
import { BodyIcon } from './BodyIcon';
import { PrimaryButton, SelectRow, CheckBadge, card } from './ui';
import { zipToStateCode, classifyCountry, regionLabel } from '../lib/zipState';
import { INSURANCE_OTHER } from '../lib/insurance';
import { useSurgeryList, catchAllName } from '../lib/surgeries';

type Commit = (patch: Answers, opts?: { advance?: boolean }) => void;

function Header({ def }: { def: ResolvedQuestion }) {
  return (
    <div style={{ marginBottom: 24 }}>
      {def.eyebrow && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: 'var(--teal-deep)',
            textTransform: 'uppercase',
            letterSpacing: '.14em',
          }}
        >
          {def.eyebrow}
        </div>
      )}
      <div
        className="pretty"
        style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.16, letterSpacing: '-.015em', marginTop: def.eyebrow ? 12 : 0 }}
      >
        {def.title}
      </div>
      {def.subtitle && (
        <div className="pretty" style={{ fontSize: 16, color: 'var(--slate)', fontWeight: 500, lineHeight: 1.5, marginTop: 10 }}>
          {def.subtitle}
        </div>
      )}
    </div>
  );
}

export function Question({ def, answers, onAnswer }: { def: QuestionDef; answers: Answers; onAnswer: Commit }) {
  // Resolve any country-aware copy (title/subtitle/options) against the answers so far, so
  // the sub-components only ever see concrete strings.
  const rdef = resolveQuestion(def, answers);
  switch (rdef.type) {
    case 'bodyArea':
      return <BodyAreaGrid def={rdef} value={answers[rdef.field] as string} onAnswer={onAnswer} />;
    case 'detail':
      return <DetailCards def={rdef} value={answers[rdef.field] as string} onAnswer={onAnswer} />;
    case 'single':
      return <SingleSelect def={rdef} value={answers[rdef.field] as string} onAnswer={onAnswer} />;
    case 'surgery':
      return <SurgeryPicker def={rdef} answers={answers} onAnswer={onAnswer} />;
    case 'surgeryDate':
      return <SurgeryDate def={rdef} onAnswer={onAnswer} />;
    case 'pain':
      return <PainScale def={rdef} value={answers[rdef.field] as number | undefined} onAnswer={onAnswer} />;
    case 'goal':
      return <GoalScreen def={rdef} onAnswer={onAnswer} />;
    case 'zip':
      return <ZipScreen def={rdef} onAnswer={onAnswer} />;
    case 'email':
      return <EmailScreen def={rdef} onAnswer={onAnswer} />;
    default:
      return null;
  }
}

function BodyAreaGrid({ def, value, onAnswer }: { def: ResolvedQuestion; value?: string; onAnswer: Commit }) {
  const narrow = useIsNarrow();
  return (
    <>
      <Header def={def} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {def.options!.map((o) => {
          const selected = value === o.value;
          return (
            <button
              key={o.value}
              onClick={() => onAnswer({ [def.field]: o.value }, { advance: true })}
              aria-pressed={selected}
              style={{
                ...card,
                background: selected ? 'var(--teal-tint)' : '#fff',
                boxShadow: selected ? '0 4px 24px rgba(33,172,190,.22)' : 'var(--card-shadow)',
                padding: narrow ? '12px 14px' : '14px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                textAlign: 'left',
              }}
            >
              <BodyIcon area={o.value} />
              <span style={{ fontSize: 16, fontWeight: selected ? 800 : 700, color: selected ? 'var(--teal-deep)' : 'var(--ink)', flex: 1 }}>
                {o.label}
              </span>
              {selected && <CheckBadge />}
            </button>
          );
        })}
      </div>
    </>
  );
}

function DetailCards({ def, value, onAnswer }: { def: ResolvedQuestion; value?: string; onAnswer: Commit }) {
  const narrow = useIsNarrow();
  return (
    <>
      <Header def={def} />
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 10 }}>
        {def.options!.map((o) => {
          const selected = value === o.value;
          return (
            <button
              key={o.value}
              onClick={() => onAnswer({ [def.field]: o.value }, { advance: true })}
              aria-pressed={selected}
              style={{
                ...card,
                background: selected ? 'var(--teal-tint)' : '#fff',
                boxShadow: selected ? '0 4px 24px rgba(33,172,190,.22)' : 'var(--card-shadow)',
                padding: '16px 18px',
                textAlign: 'left',
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 800, color: selected ? 'var(--teal-deep)' : 'var(--ink)' }}>{o.label}</div>
              {o.sublabel && <div style={{ fontSize: 13, color: 'var(--slate)', fontWeight: 600, marginTop: 4 }}>{o.sublabel}</div>}
            </button>
          );
        })}
      </div>
    </>
  );
}

function SingleSelect({ def, value, onAnswer }: { def: ResolvedQuestion; value?: string; onAnswer: Commit }) {
  // Insurance "Other" reveals a free-text field instead of advancing immediately.
  const [otherText, setOtherText] = useState('');
  const isInsuranceOther = def.id === 'insuranceProvider';
  return (
    <>
      <Header def={def} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {def.options!.map((o) => {
          const selected = value === o.value || (isInsuranceOther && o.value === INSURANCE_OTHER && value != null && !def.options!.some((x) => x.value === value));
          const isOther = isInsuranceOther && o.value === INSURANCE_OTHER;
          return (
            <div key={o.value}>
              <SelectRow
                label={o.label}
                sublabel={o.sublabel}
                selected={!!selected}
                onSelect={() => {
                  if (isOther) {
                    // reveal free-text; don't advance until they type + confirm
                    onAnswer({ [def.field]: otherText || '' });
                  } else {
                    onAnswer({ [def.field]: o.value }, { advance: true });
                  }
                }}
              />
              {isOther && selected && (
                <div style={{ marginTop: 9, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    autoFocus
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    placeholder="Your insurance provider"
                    style={{ ...card, flex: 1, padding: '15px 18px', fontSize: 16, fontWeight: 600 }}
                  />
                  <PrimaryButton disabled={!otherText.trim()} onClick={() => onAnswer({ [def.field]: otherText.trim() }, { advance: true })}>
                    Continue
                  </PrimaryButton>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// Must match the API's cap (sanitizeIntakeInput SURGERY_DETAIL_MAX).
const SURGERY_DETAIL_MAX = 200;

// "Which surgery?": the catalogue's surgeries for the area already chosen (lib/surgeries),
// then "Another surgery", which records the area's catch-all row and opens an optional box
// for the surgery's name. A listed surgery advances on tap like any single-select step;
// "Another surgery" waits for Continue. An area with nothing listed (neck, upper back) goes
// straight to the box rather than offering a list of one. The typed name is saved to the
// intake as surgeryDetail and goes nowhere else.
function SurgeryPicker({ def, answers, onAnswer }: { def: ResolvedQuestion; answers: Answers; onAnswer: Commit }) {
  const bodyPart = answers.bodyPart as string | undefined;
  // Only Back has a detail question (upper / lower); any other area's is stale.
  const backDetail = bodyPart === 'Back' && typeof answers.bodyPartDetail === 'string' ? answers.bodyPartDetail : undefined;
  const { list, builtIn, hold } = useSurgeryList(bodyPart, backDetail);
  const [typed, setTyped] = useState<string | null>(null);
  const chosen = answers[def.field] as string | undefined;

  if (!list) {
    return (
      <>
        <Header def={def} />
        <div style={{ ...card, padding: '17px 20px', fontSize: 14, fontWeight: 600, color: 'var(--slate)' }}>Loading surgeries…</div>
      </>
    );
  }

  const other = catchAllName(list);
  const nothingListed = list.surgeries.length === 0;
  // A name typed earlier shows only when the saved answer is this area's "Another surgery"
  // (coming back to it); choosing it afresh clears it (see the row below).
  const detail = typed ?? (chosen === other && typeof answers.surgeryDetail === 'string' ? answers.surgeryDetail : '');
  const continueOther = () => onAnswer({ [def.field]: other, surgeryDetail: detail.trim() || null }, { advance: true });

  const detailBox = (
    <div style={{ marginTop: 9 }}>
      <div style={{ ...card, padding: '16px 18px' }}>
        <label
          htmlFor="surgery-detail"
          style={{ display: 'block', fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.12em' }}
        >
          Surgery name (optional)
        </label>
        <input
          id="surgery-detail"
          autoFocus={!nothingListed}
          value={detail}
          maxLength={SURGERY_DETAIL_MAX}
          onChange={(e) => {
            hold();
            setTyped(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') continueOther();
          }}
          placeholder="The name your surgeon used"
          style={{ border: 'none', outline: 'none', width: '100%', fontSize: 16, fontWeight: 600, marginTop: 7, background: 'transparent' }}
        />
      </div>
      <div style={{ marginTop: 16 }}>
        <PrimaryButton onClick={continueOther}>Continue</PrimaryButton>
      </div>
    </div>
  );

  if (nothingListed) {
    // The built-in list has nothing for most areas, so it must not claim the catalogue does not either.
    const area = bodyPart ? `${backDetail ? `${backDetail.toLowerCase()} ` : ''}${bodyPart.toLowerCase()} ` : '';
    const subtitle = builtIn
      ? "Tell us which one, if you know, and we'll help you get the right plan."
      : `We don't list ${area}surgeries yet. Tell us which one, if you know, and we'll help you get the right plan.`;
    return (
      <>
        <Header def={{ ...def, subtitle }} />
        {detailBox}
      </>
    );
  }

  return (
    <>
      <Header def={def} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {list.surgeries.map((s) => (
          <SelectRow
            key={s.name}
            label={s.name}
            selected={chosen === s.name}
            onSelect={() => onAnswer({ [def.field]: s.name, surgeryDetail: null }, { advance: true })}
          />
        ))}
        <div>
          {/* Recorded on tap, like insurance "Other"; Continue then advances. A fresh choice
              starts with an empty box: a name typed earlier was for another answer. */}
          <SelectRow
            label="Another surgery"
            selected={chosen === other}
            onSelect={() => {
              hold();
              if (chosen !== other) onAnswer({ [def.field]: other, surgeryDetail: null });
            }}
          />
          {chosen === other && detailBox}
        </div>
      </div>
    </>
  );
}

function SurgeryDate({ def, onAnswer }: { def: ResolvedQuestion; onAnswer: Commit }) {
  const [date, setDate] = useState('');
  const [notScheduled, setNotScheduled] = useState(false);
  const canContinue = notScheduled || !!date;
  return (
    <>
      <Header def={def} />
      <div style={{ ...card, padding: '6px 8px' }}>
        <input
          type="date"
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
            setNotScheduled(false);
          }}
          style={{ border: 'none', outline: 'none', width: '100%', padding: '14px 12px', fontSize: 18, fontWeight: 800, background: 'transparent' }}
        />
      </div>
      <div style={{ marginTop: 9 }}>
        <SelectRow label="Not scheduled yet" selected={notScheduled} onSelect={() => { setNotScheduled(true); setDate(''); }} />
      </div>
      <div style={{ marginTop: 24 }}>
        <PrimaryButton disabled={!canContinue} onClick={() => onAnswer({ [def.field]: notScheduled ? null : date }, { advance: true })}>
          Continue
        </PrimaryButton>
      </div>
    </>
  );
}

const PAIN_LABELS = [
  'No pain at all', 'Barely there', 'Mild, easy to ignore', 'Noticeable now and then',
  'Noticeable, but manageable', 'Hard to ignore', 'Interrupts what I’m doing',
  'Hard to focus through', 'Intense', 'Almost unbearable', 'Worst imaginable',
];

function PainScale({ def, value, onAnswer }: { def: ResolvedQuestion; value?: number; onAnswer: Commit }) {
  const [pain, setPain] = useState<number | null>(value ?? null);
  return (
    <>
      <Header def={def} />
      <div style={{ ...card, borderRadius: 14, padding: '24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 8 }}>
          <span style={{ fontSize: 52, fontWeight: 800, color: 'var(--teal)', lineHeight: 1 }}>{pain ?? '-'}</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--slate)' }}>/ 10</span>
        </div>
        <div style={{ textAlign: 'center', fontSize: 14, fontWeight: 700, color: 'var(--teal-deep)', marginTop: 6, minHeight: 20 }}>
          {pain != null ? PAIN_LABELS[pain] : 'Tap a number'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(11, 1fr)', gap: 4, marginTop: 20 }}>
          {Array.from({ length: 11 }, (_, n) => {
            const active = pain === n;
            return (
              <button
                key={n}
                onClick={() => setPain(n)}
                aria-label={`Pain ${n}`}
                style={{
                  height: 46,
                  borderRadius: 8,
                  background: active ? 'var(--teal)' : '#F1F4F5',
                  color: active ? '#fff' : '#9CA3AF',
                  fontSize: active ? 16 : 13,
                  fontWeight: active ? 800 : 700,
                  boxShadow: active ? '0 4px 14px rgba(33,172,190,.4)' : 'none',
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#9CA3AF' }}>No pain</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#9CA3AF' }}>Worst imaginable</span>
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <PrimaryButton full disabled={pain == null} onClick={() => onAnswer({ [def.field]: pain }, { advance: true })}>
          Continue
        </PrimaryButton>
      </div>
    </>
  );
}

function GoalScreen({ def, onAnswer }: { def: ResolvedQuestion; onAnswer: Commit }) {
  const narrow = useIsNarrow();
  const [pick, setPick] = useState<string | null>(null);
  const [text, setText] = useState('');
  const canContinue = !!pick || !!text.trim();
  // The goal is the engagement moment — the design lifts the whole screen onto a
  // teal-tint surface so it reads as the reward, not another field.
  return (
    <div style={def.tinted ? { background: 'var(--teal-tint)', borderRadius: 16, padding: 24, margin: '-4px -8px' } : undefined}>
      <Header def={def} />
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 9 }}>
        {def.options!.map((o) => {
          const selected = pick === o.value;
          return (
            <button
              key={o.value}
              onClick={() => setPick(o.value)}
              aria-pressed={selected}
              style={{
                borderRadius: 12,
                padding: 18,
                textAlign: 'left',
                background: selected ? 'var(--teal)' : '#fff',
                boxShadow: selected ? '0 6px 24px rgba(33,172,190,.34)' : 'var(--card-shadow)',
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 800, color: selected ? '#fff' : 'var(--ink)' }}>{o.label}</div>
              {o.sublabel && (
                <div style={{ fontSize: 13, fontWeight: 600, marginTop: 3, color: selected ? '#fff' : 'var(--slate)', opacity: selected ? 0.9 : 1 }}>
                  {o.sublabel}
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ ...card, padding: '16px 18px', marginTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.12em' }}>
          In your own words (optional)
        </div>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Hike the Catskills with my daughter in October."
          style={{ border: 'none', outline: 'none', width: '100%', fontSize: 16, fontWeight: 600, marginTop: 7, background: 'transparent' }}
        />
      </div>
      <div style={{ marginTop: 16 }}>
        <PrimaryButton full disabled={!canContinue} onClick={() => onAnswer({ [def.field]: text.trim() || pick }, { advance: true })}>
          That's my goal
        </PrimaryButton>
      </div>
    </div>
  );
}

function ZipScreen({ def, onAnswer }: { def: ResolvedQuestion; onAnswer: Commit }) {
  const [zip, setZip] = useState('');
  // US ZIP or UK postcode. UK codes have letters, so allow alphanumeric + space. State is
  // only resolved (and only relevant) for US — the UK has no state-by-state licensure rule.
  const country = classifyCountry(zip);
  const label = regionLabel(zip);
  const stateCode = country === 'US' ? zipToStateCode(zip) : null;
  return (
    <>
      <Header def={def} />
      <div style={{ ...card, padding: '6px 8px', display: 'flex', alignItems: 'center' }}>
        <input
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="postal-code"
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').toUpperCase().slice(0, 8))}
          placeholder="Zip Code / Postcode"
          style={{ border: 'none', outline: 'none', width: '100%', padding: '14px 12px', fontSize: 18, fontWeight: 800, background: 'transparent' }}
        />
        {label && <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--teal-deep)', paddingRight: 14, whiteSpace: 'nowrap' }}>{label}</span>}
      </div>
      <div style={{ marginTop: 24 }}>
        <PrimaryButton
          disabled={!country}
          onClick={() => onAnswer({ zip: zip.trim(), state: stateCode ?? undefined, country: country ?? undefined }, { advance: true })}
        >
          Continue
        </PrimaryButton>
      </div>
    </>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function EmailScreen({ def, onAnswer }: { def: ResolvedQuestion; onAnswer: Commit }) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [disclaimer, setDisclaimer] = useState(false);
  const valid = EMAIL_RE.test(email) && consent && disclaimer;
  return (
    <>
      <Header def={def} />
      <div style={{ ...card, padding: '6px 8px' }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          style={{ border: 'none', outline: 'none', width: '100%', padding: '14px 12px', fontSize: 16, fontWeight: 600, background: 'transparent' }}
        />
      </div>
      <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 18, cursor: 'pointer' }}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} />
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.45 }}>
          Email me my plan and recovery reminders. Unsubscribe any time.
        </span>
      </label>
      <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={disclaimer} onChange={(e) => setDisclaimer(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} />
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.45 }}>
          I understand Expy Health provides guided exercise, not a medical diagnosis or treatment, and that I should talk to my clinician about my care.
        </span>
      </label>
      <div style={{ marginTop: 24 }}>
        <PrimaryButton
          full
          disabled={!valid}
          onClick={() => onAnswer({ email, emailConsent: consent, disclaimerAccepted: disclaimer, completed: true }, { advance: true })}
        >
          See my plan
        </PrimaryButton>
        {!valid && (
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--slate)', marginTop: 10, textAlign: 'center' }}>
            Enter your email and tick both boxes to continue. Neither is pre-ticked, and both are required.
          </div>
        )}
      </div>
    </>
  );
}
